import { Injectable } from '@angular/core';
import Pusher, { type Channel } from 'pusher-js';
import { Observable } from 'rxjs';

export enum PUSHER_CHANNEL_KEY {
  presenceChannelShuttle = 'channel-shuttle',
}

export const PUSHER_CHANNELS: PUSHER_CHANNEL_KEY[] = [
  PUSHER_CHANNEL_KEY.presenceChannelShuttle,
];

@Injectable({
  providedIn: 'root',
})
export class PusherClient {
  private pusher?: Pusher;
  private channels?: Record<PUSHER_CHANNEL_KEY, Channel>;

  private getTodayEventName(date: Date = new Date()): string {
    return date.toISOString().slice(0, 10); // YYYY-MM-DD
  }

  initPusher(): void {
    try {
      const pusherKey = import.meta.env.NG_APP_PUSHER_KEY;
      if (
        !pusherKey ||
        typeof pusherKey !== 'string' ||
        pusherKey.trim() === ''
      ) {
        throw new Error(
          'Missing or empty NG_APP_PUSHER_KEY environment variable. Pusher initialization aborted.',
        );
      }

      this.pusher = new Pusher(import.meta.env.NG_APP_PUSHER_KEY, {
        cluster: import.meta.env.NG_APP_PUSHER_CLUSTER,
        enableStats: true,
      });

      this.channels = {
        [PUSHER_CHANNEL_KEY.presenceChannelShuttle]: this.pusher.subscribe(
          PUSHER_CHANNEL_KEY.presenceChannelShuttle,
        ),
      };
    } catch (error) {
      console.error('Error initializing Pusher:', error);
    }
  }

  subscribeShuttlePresenceToday(
    date: Date = new Date(),
  ): Observable<{ ride_id: number }> {
    const channel = this.channels?.[PUSHER_CHANNEL_KEY.presenceChannelShuttle];

    if (channel) {
      return new Observable<{ ride_id: number }>((observer) => {
        if (!channel) {
          observer.error(
            new Error(
              'Pusher channel not initialized. Initialization may have failed.',
            ),
          );
          return;
        }

        const eventName = this.getTodayEventName(date);
        const handler = (data: { ride_id: number }) => observer.next(data);

        channel.bind(eventName, handler);

        return () => {
          channel.unbind(eventName, handler);
        };
      });
    }

    throw new Error('Channel not found');
  }

  unsubscribeChannel(channelName: PUSHER_CHANNEL_KEY): void {
    if (this.pusher) {
      this.pusher.unsubscribe(channelName);
    }
  }

  disconnect(): void {
    if (this.pusher) {
      this.pusher.disconnect();
    }
  }
}
